/**
 * Agency Portal v2 Slice 8 — "White label" editor rewrite.
 *
 * Two-column layout: appearance controls on the LEFT (360 px), full
 * preview on the RIGHT (flex). The preview has two tabs:
 *
 *   - "Report page" — renders the dashboard-local
 *     `WhiteLabelReportPreview` component (apps/marketing can't be
 *     imported here) against a canned sample. Reflects the unsaved
 *     form state as the agency types.
 *   - "Email" — fetches the real `buildReadinessReportEmail` HTML
 *     from `POST /v1/account/white-label/preview-email` and renders
 *     it inside a sandboxed iframe (`sandbox=""`, srcdoc).
 *
 * Accent is a native `<input type="color">` + a hex text field, kept
 * in sync. Contrast is assessed against ink + white; the editor warns
 * on low contrast but never blocks save. `contact_url` adds the "Talk
 * to {Agency}" CTA target. The SaveBar handles save state.
 *
 * Below 1024 px the preview stacks under the controls (CSS Grid).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useOptionalAuth } from '../../app/auth-context.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { useAgency } from '../../app/agency-context.js';
import {
  getWhiteLabel,
  putWhiteLabel,
  previewWhiteLabelEmail,
  type PutWhiteLabelInput,
  type ReportCtaMode,
  type WhiteLabelWire,
} from '../../api/account-white-label.js';
import { getAgencyReferral } from '../../api/agency-referral.js';
import { useC } from '../../lib/theme.js';
import {
  Button,
  Card,
  InlineNotice,
  NumLabel,
  SaveBar,
  type SaveBarStatus,
} from '../../components/atoms/index.js';
import { useIsEmbedded } from '../agency-portal/portal-shell-context.js';
import { mapReason } from '../../lib/humanize-agency-error.js';
import { WhiteLabelReportPreview } from './WhiteLabelReportPreview.js';
import { wcagAssessAgainstTheme } from './wl-contrast.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; state: WhiteLabelWire | null }
  | { kind: 'error'; message: string };

type PreviewTab = 'report' | 'email';

/** Strict `#rrggbb`. */
const CLIENT_HEX_RE = /^#[0-9a-f]{6}$/i;

function clientValidateLogoUrl(raw: string): 'ok' | 'empty' | 'bad' {
  const trimmed = raw.trim();
  if (trimmed === '') return 'empty';
  const lowered = trimmed.toLowerCase();
  if (
    lowered.startsWith('javascript:')
    || lowered.startsWith('data:')
    || lowered.startsWith('blob:')
    || lowered.startsWith('file:')
    || lowered.startsWith('//')
    || lowered.startsWith('/')
    || !lowered.startsWith('https://')
  ) {
    return 'bad';
  }
  if (trimmed.length > 2048) return 'bad';
  return 'ok';
}

function clientValidateContactUrl(raw: string): 'ok' | 'empty' | 'bad' {
  const trimmed = raw.trim();
  if (trimmed === '') return 'empty';
  const lowered = trimmed.toLowerCase();
  if (lowered.startsWith('mailto:')) {
    // local@domain.tld (local part may include common special chars).
    return /^mailto:[^\s<>"@]+@[^\s<>"@]+\.[^\s<>"@]+$/.test(trimmed) ? 'ok' : 'bad';
  }
  if (
    !lowered.startsWith('https://')
    || lowered.startsWith('//')
    || lowered.startsWith('/')
  ) {
    return 'bad';
  }
  if (trimmed.length > 2048) return 'bad';
  return 'ok';
}

/** v3 (2026-10-10) — wrap the preview email HTML with a `color-scheme`
 *  meta hint so the dashboard preview iframe never flips to the
 *  email's dark-mode media query even when the dashboard runs in
 *  dark theme. The email body itself keeps its dark-mode support
 *  for recipients; this wrapper only pins the dashboard preview. */
function wrapEmailPreviewLight(innerHtml: string): string {
  // Fast path: `<head>` present → inject meta + root color-scheme CSS.
  const metaTag = '<meta name="color-scheme" content="only light">';
  const style = '<style>:root{color-scheme:only light}html,body{color-scheme:only light;background:#ffffff}</style>';
  if (/<head[\s>]/i.test(innerHtml)) {
    return innerHtml.replace(/<head([^>]*)>/i, (full) => `${full}${metaTag}${style}`);
  }
  // Fallback: no `<head>` — prepend a minimal wrapper. The sandbox
  // iframe parses it as a document regardless.
  return `<!doctype html><html><head>${metaTag}${style}</head><body>${innerHtml}</body></html>`;
}

/** Expand `#abc` → `#aabbcc` on blur so the hex input is forgiving. */
function expandShorthandHex(raw: string): string {
  const trimmed = raw.trim();
  if (/^#[0-9a-f]{3}$/i.test(trimmed)) {
    const r = trimmed[1]!;
    const g = trimmed[2]!;
    const b = trimmed[3]!;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return trimmed;
}

interface Draft {
  readonly displayName: string;
  readonly logoUrl: string;
  readonly accentHex: string;
  readonly contactUrl: string;
  /** Agency Portal v3 §5.4 — selected CTA on branded reports. */
  readonly ctaMode: ReportCtaMode | null;
}

function stateToDraft(s: WhiteLabelWire | null): Draft {
  return {
    displayName: s?.display_name ?? '',
    logoUrl: s?.logo_url ?? '',
    accentHex: s?.accent_hex ?? '',
    contactUrl: s?.contact_url ?? '',
    ctaMode: s?.report_cta_mode ?? null,
  };
}

function draftsEqual(a: Draft, b: Draft): boolean {
  return (
    a.displayName.trim() === b.displayName.trim()
    && a.logoUrl.trim() === b.logoUrl.trim()
    && a.accentHex.trim().toLowerCase() === b.accentHex.trim().toLowerCase()
    && a.contactUrl.trim() === b.contactUrl.trim()
    && (a.ctaMode ?? null) === (b.ctaMode ?? null)
  );
}

export function WhiteLabel(): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const { features, acting } = useAgency();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [draft, setDraft] = useState<Draft>({
    displayName: '',
    logoUrl: '',
    accentHex: '',
    contactUrl: '',
    ctaMode: null,
  });
  const [serverDraft, setServerDraft] = useState<Draft>({
    displayName: '',
    logoUrl: '',
    accentHex: '',
    contactUrl: '',
    ctaMode: null,
  });
  const [tab, setTab] = useState<PreviewTab>('report');
  const [saveStatus, setSaveStatus] = useState<SaveBarStatus>('clean');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [emailHtml, setEmailHtml] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const emailAbort = useRef<AbortController | null>(null);

  // v3 (2026-10-10) — read the authed account name so we can show the
  // "Leave empty to use …" hint. The dashboard already fetches this
  // through the Auth context; falling back to a generic placeholder
  // keeps the UI honest if the context isn't ready yet.
  const auth = useOptionalAuth();
  const accountName =
    auth?.state.kind === 'authenticated'
      ? auth.state.user.home_account_name ?? null
      : null;

  // v3.2 (2026-10-10) §5.4 — the Report-button radios need to know
  // whether the agency already has a referral slug (the "Sign-up
  // button" option depends on one existing to attribute signups). We
  // read it once on mount so the picker can show a prompt instead of
  // silently letting the agency pick an option that will fall back.
  //
  // v4 (2026-10-10) — initial value is `undefined`, NOT null. The
  // "Report page" preview smart-default picks `claim_sill` when a slug
  // is loaded; before the fetch resolves we don't want the preview to
  // flash through `none`/`custom` and only land on the real selection
  // after the agency clicks the tab (that was the "button not showing
  // until I toggle" bug). A `undefined` value is treated as "slug not
  // yet known" by the preview (it short-circuits with the smart-
  // default that uses `null` → still no CTA button). The email preview
  // effect gates on this value so no fetch fires before we know.
  const [referralSlug, setReferralSlug] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    if (http === null) return;
    let cancelled = false;
    void (async () => {
      const r = await getAgencyReferral(http);
      if (cancelled) return;
      // v4 — ALWAYS resolve to a concrete value (null when the agency
      // has no slug yet); never leave the state at `undefined`, else
      // the email preview never fires.
      if (r.kind === 'ok') setReferralSlug(r.state.slug);
      else setReferralSlug(null);
    })();
    return () => {
      cancelled = true;
    };
  }, [http]);

  const refresh = useCallback(async (): Promise<void> => {
    if (http === null) return;
    setState({ kind: 'loading' });
    const r = await getWhiteLabel(http);
    if (r.kind === 'ok') {
      setState({ kind: 'ok', state: r.state });
      const d = stateToDraft(r.state);
      setDraft(d);
      setServerDraft(d);
      setSaveStatus('clean');
    } else if (r.kind === 'not_set') {
      setState({ kind: 'ok', state: null });
      const d = stateToDraft(null);
      setDraft(d);
      setServerDraft(d);
      setSaveStatus('clean');
    } else if (r.kind === 'forbidden') {
      setState({ kind: 'error', message: 'Agency access is not available on this account.' });
    } else {
      setState({ kind: 'error', message: r.message });
    }
  }, [http]);

  useEffect(() => {
    if (features === null) return;
    if (features.agency !== true) return;
    if (acting !== null) return;
    void refresh();
  }, [features, acting, refresh]);

  // Mark the form dirty when the draft diverges from the server state.
  useEffect(() => {
    if (saveStatus === 'saving') return;
    const dirty = !draftsEqual(draft, serverDraft);
    setSaveStatus((prev) => {
      if (dirty) return prev === 'saved' ? 'dirty' : (prev === 'clean' ? 'dirty' : prev);
      if (prev === 'dirty' || prev === 'error') return 'clean';
      return prev;
    });
  }, [draft, serverDraft, saveStatus]);

  // Debounce the email-preview fetch — only fire when the Email tab is
  // active and the draft is valid. 300 ms matches the §4.7 spec. v3
  // (2026-10-10) — on an invalid intermediate state we KEEP the last
  // successful HTML on screen and show a small amber "Preview paused"
  // hint, instead of flipping to a dead "Rendering preview…" box when
  // the agency types a reserved brand token.
  useEffect(() => {
    if (http === null) return;
    if (tab !== 'email') return;
    // v4 (2026-10-10) — wait for the referral slug fetch to resolve
    // before firing the preview. The server-side preview reads the
    // slug from the DB and renders the Claim button when it exists;
    // if the dashboard renders a preview BEFORE the slug has loaded
    // and the agency later clicks a different tab, the second render
    // (slug now present) suddenly shows the Claim button — the
    // founder's "button appears only after I toggle" symptom.
    //
    // When referralSlug is undefined the fetch has not resolved yet.
    // When it resolves (either to a string or null) this effect
    // re-fires with the right value AND the server reads the DB
    // (which also agrees). No chance of a stale preview on first
    // render.
    if (referralSlug === undefined) return;
    const nameOk = draft.displayName.trim().length > 0;
    const logoOk =
      draft.logoUrl.trim() === '' || clientValidateLogoUrl(draft.logoUrl) === 'ok';
    if (!nameOk || !logoOk) {
      // Keep the last HTML; just mark the preview paused.
      setEmailError(nameOk ? 'Preview paused: logo URL must be https://.' : null);
      return;
    }
    const ctrl = new AbortController();
    emailAbort.current?.abort();
    emailAbort.current = ctrl;
    const t = setTimeout(() => {
      void (async () => {
        const r = await previewWhiteLabelEmail(http, {
          display_name: draft.displayName.trim(),
          logo_url: draft.logoUrl.trim() === '' ? null : draft.logoUrl.trim(),
          accent_hex: CLIENT_HEX_RE.test(draft.accentHex.trim())
            ? draft.accentHex.trim().toLowerCase()
            : null,
          contact_url: draft.contactUrl.trim() === '' ? null : draft.contactUrl.trim(),
          report_cta_mode: draft.ctaMode,
        });
        if (ctrl.signal.aborted) return;
        if (r.kind === 'ok') {
          setEmailHtml(r.html);
          setEmailError(null);
        } else if (r.kind === 'invalid') {
          // v3 — the latest draft is invalid (reserved brand, bad hex,
          // etc). Keep the last good HTML on screen; show a friendly
          // paused hint instead of clearing to a stuck placeholder.
          setEmailError('Preview paused: check the fields on the left.');
        } else if (r.kind === 'rate_limited') {
          setEmailError('Preview is cooling down. Pause briefly and try again.');
        } else {
          setEmailError('Preview is temporarily unavailable.');
        }
      })();
    }, 300);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [http, tab, draft.displayName, draft.logoUrl, draft.accentHex, draft.contactUrl, draft.ctaMode, referralSlug]);

  const canSave =
    features !== null
    && features.agency === true
    && saveStatus !== 'saving'
    && saveStatus !== 'clean'
    && draft.displayName.trim().length >= 1
    && draft.displayName.trim().length <= 80
    && (draft.logoUrl.trim() === '' || clientValidateLogoUrl(draft.logoUrl) === 'ok')
    && (draft.accentHex.trim() === '' || CLIENT_HEX_RE.test(draft.accentHex.trim()))
    && (draft.contactUrl.trim() === '' || clientValidateContactUrl(draft.contactUrl) === 'ok');

  const onSave = useCallback(async (): Promise<void> => {
    if (http === null) return;
    setSaveStatus('saving');
    setSaveError(null);
    const body: PutWhiteLabelInput = {
      display_name: draft.displayName.trim(),
      logo_url: draft.logoUrl.trim() === '' ? null : draft.logoUrl.trim(),
      accent_hex: draft.accentHex.trim() === '' ? null : draft.accentHex.trim().toLowerCase(),
      contact_url: draft.contactUrl.trim() === '' ? null : draft.contactUrl.trim(),
      report_cta_mode: draft.ctaMode,
    };
    const r = await putWhiteLabel(http, body);
    if (r.kind === 'ok') {
      setSavedAt(new Date());
      setSaveStatus('saved');
      await refresh();
      return;
    }
    setSaveStatus('error');
    // v3 (2026-10-10) — single source of truth for the "reason →
    // friendly copy" mapping (shared with every other agency surface).
    // `invalid_display_name` carries the raw reason (`reserved_brand`
    // vs. the generic validation failure) so the agency sees the
    // specific "name is too close to a protected brand" copy, not a
    // one-size-fits-all control-character message.
    switch (r.kind) {
      case 'invalid_display_name':
        setSaveError(mapReason(r.reason) ?? mapReason('invalid_display_name') ?? 'Agency name is not valid.');
        break;
      case 'invalid_logo_url':
        setSaveError(mapReason('invalid_logo_url') ?? 'Logo URL must be https://.');
        break;
      case 'invalid_accent_hex':
        setSaveError(mapReason('invalid_accent_hex') ?? 'Accent must be a 6-digit hex colour.');
        break;
      case 'invalid_contact_url':
        setSaveError(mapReason('invalid_contact_url') ?? 'Contact link must be https:// or mailto:.');
        break;
      case 'invalid_cta_mode':
        setSaveError(mapReason('invalid_cta_mode') ?? 'Pick one of the Report-button options.');
        break;
      case 'forbidden':
        setSaveError(mapReason(r.reason) ?? 'You do not have permission to edit this.');
        break;
      default:
        setSaveError(mapReason(r.message) ?? 'Could not save. Try again, or contact Sill if it keeps failing.');
    }
  }, [http, draft, refresh]);

  const onDiscard = useCallback(() => {
    setDraft(serverDraft);
    setSaveStatus('clean');
    setSaveError(null);
  }, [serverDraft]);

  const whiteLabelOn = features?.white_label === true;
  const contrast = useMemo(() => {
    if (draft.accentHex.trim() === '' || !CLIENT_HEX_RE.test(draft.accentHex.trim())) return null;
    return wcagAssessAgainstTheme(draft.accentHex.trim().toLowerCase());
  }, [draft.accentHex]);

  // Guardrails ------------------------------------------------------
  if (features === null) {
    return (
      <WhiteLabelShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
            Loading…
          </div>
        </Card>
      </WhiteLabelShell>
    );
  }
  if (acting !== null) {
    return (
      <WhiteLabelShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
            Return to your home account to manage white label.
          </div>
        </Card>
      </WhiteLabelShell>
    );
  }
  if (features.agency !== true) {
    return (
      <WhiteLabelShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.5, marginBottom: 8 }}>
            White label is an agency add-on.
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
            Contact Sill to enable the agency surface on this account.
          </div>
        </Card>
      </WhiteLabelShell>
    );
  }
  if (state.kind === 'loading') {
    return (
      <WhiteLabelShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
            Loading…
          </div>
        </Card>
      </WhiteLabelShell>
    );
  }
  if (state.kind === 'error') {
    return (
      <WhiteLabelShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>{state.message}</div>
        </Card>
      </WhiteLabelShell>
    );
  }

  return (
    <WhiteLabelShell>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 360px) minmax(0, 1fr)',
          gap: 20,
        }}
        className="wl-grid"
      >
        {/* ─── LEFT: appearance controls ───────────────────────── */}
        <Card className="p-5 sm:p-6" data-testid="white-label-appearance">
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 12,
            }}
          >
            Appearance
          </div>

          {/* Display name */}
          <label htmlFor="wl-display-name" style={labelStyle(C)}>Agency name</label>
          <input
            id="wl-display-name"
            type="text"
            data-testid="white-label-display-name"
            maxLength={80}
            value={draft.displayName}
            onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value }))}
            style={inputStyle(C)}
          />
          <div style={hintStyle(C)}>
            {/* v3 (2026-10-10) — explicit fallback: if the agency
                leaves this blank, reports render with their account
                name. Non-technical owners asked where their name on
                reports came from when they left the field empty. */}
            Shown on reports and as "{draft.displayName.trim() || accountName || 'Your agency'} via Sill" in the email From line.
            {draft.displayName.trim() === '' && accountName !== null ? (
              <>
                {' '}Leave empty to use <strong>{accountName}</strong>.
              </>
            ) : null}
          </div>

          {/* Logo URL */}
          <label htmlFor="wl-logo-url" style={{ ...labelStyle(C), marginTop: 16 }}>Logo URL (optional)</label>
          <input
            id="wl-logo-url"
            type="url"
            data-testid="white-label-logo-url"
            maxLength={2048}
            placeholder="https://cdn.example.com/logo.png"
            value={draft.logoUrl}
            onChange={(e) => setDraft((d) => ({ ...d, logoUrl: e.target.value }))}
            style={{ ...inputStyle(C), fontFamily: monoFont, fontSize: 13 }}
          />
          <div
            style={hintStyle(C)}
            data-testid="white-label-logo-privacy-note"
          >
            Loads from your server. Your host can see when a report or email is opened.
          </div>

          {/* Accent — swatch button + native color + hex text */}
          <label htmlFor="wl-accent-hex" style={{ ...labelStyle(C), marginTop: 16 }}>Accent colour (optional)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
            <label
              htmlFor="wl-accent-color-picker"
              aria-label="Open colour picker"
              style={{
                position: 'relative',
                display: 'inline-block',
                width: 28,
                height: 28,
                borderRadius: 2,
                border: `1px solid ${C.border}`,
                background: CLIENT_HEX_RE.test(draft.accentHex.trim()) ? draft.accentHex.trim() : C.surface2,
                cursor: 'pointer',
                flex: '0 0 auto',
              }}
            >
              <input
                id="wl-accent-color-picker"
                type="color"
                data-testid="white-label-accent-picker"
                value={CLIENT_HEX_RE.test(draft.accentHex.trim()) ? draft.accentHex.trim().toLowerCase() : '#d9ff3f'}
                onChange={(e) => setDraft((d) => ({ ...d, accentHex: e.target.value.toLowerCase() }))}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer',
                  border: 0,
                  padding: 0,
                }}
              />
            </label>
            <input
              id="wl-accent-hex"
              type="text"
              data-testid="white-label-accent-hex"
              maxLength={7}
              placeholder="#d9ff3f"
              value={draft.accentHex}
              onChange={(e) => setDraft((d) => ({ ...d, accentHex: e.target.value }))}
              onBlur={() => setDraft((d) => ({ ...d, accentHex: expandShorthandHex(d.accentHex).toLowerCase() }))}
              style={{ ...inputStyle(C), width: 160, fontFamily: monoFont, fontSize: 13, marginTop: 0 }}
            />
            {draft.accentHex.trim() !== '' ? (
              <Button
                variant="ghost"
                size="sm"
                data-testid="white-label-accent-reset"
                onClick={() => setDraft((d) => ({ ...d, accentHex: '' }))}
              >
                Reset
              </Button>
            ) : null}
          </div>
          {contrast !== null && contrast.tier !== 'pass_aa' ? (
            <div style={{ marginTop: 8 }}>
              <InlineNotice tone="warn" data-testid="white-label-accent-contrast-warn">
                Low contrast on button text. Readers may find it hard to read; Sill falls back to lime if it fails.
              </InlineNotice>
            </div>
          ) : null}
          <div style={hintStyle(C)}>
            Used on links and the "Talk to {draft.displayName.trim() || 'you'}" button. Sill lime stays the primary colour for pass marks.
          </div>

          {/* Contact URL */}
          <label htmlFor="wl-contact-url" style={{ ...labelStyle(C), marginTop: 16 }}>Contact link (optional)</label>
          <input
            id="wl-contact-url"
            type="url"
            data-testid="white-label-contact-url"
            maxLength={2048}
            placeholder="https://your-agency.example/contact"
            value={draft.contactUrl}
            onChange={(e) => setDraft((d) => ({ ...d, contactUrl: e.target.value }))}
            style={{ ...inputStyle(C), fontFamily: monoFont, fontSize: 13 }}
          />
          <div style={hintStyle(C)}>
            Where the report's "Talk to {draft.displayName.trim() || 'your agency'}" button goes. `mailto:` also works.
          </div>

          {/* v3 (2026-10-10) §5.4 — Report-button mode. Founder-
              requested: an agency can either (a) make the report CTA
              a "Claim" button that attributes signups back to them,
              (b) use their own contact link, or (c) hide the button
              entirely. */}
          {/* v3.2 (2026-10-10) — three options only; Default option
              dropped. If `draft.ctaMode` is null (fresh row that has
              never been saved), the "effective" default below is
              computed from the inputs (slug → claim_sill, contact →
              custom, else → none). When the agency picks "Sign-up
              button" without a referral link, we show an inline
              prompt pointing them to the Referral section instead of
              silently falling back on the server. */}
          {(() => {
            // v4 — treat `undefined` (slug fetch not yet resolved) as
            // "no slug yet"; this never flips the preview after mount.
            const slugLoaded =
              typeof referralSlug === 'string' && referralSlug.length > 0;
            const effectiveCta: ReportCtaMode =
              draft.ctaMode ??
              (slugLoaded
                ? 'claim_sill'
                : draft.contactUrl.trim().length > 0
                  ? 'custom'
                  : 'none');
            const needsSlugPrompt = effectiveCta === 'claim_sill' && !slugLoaded;
            return (
              <div style={{ marginTop: 18 }} data-testid="white-label-cta-mode">
                <div style={labelStyle(C)}>Report button</div>
                <div style={hintStyle(C)}>
                  Pick which button appears on the reports you share.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                  <CtaRadio
                    C={C}
                    value="claim_sill"
                    selected={effectiveCta}
                    onChange={(v) => setDraft((d) => ({ ...d, ctaMode: v }))}
                    label="Sign-up button (credited to you)"
                    subline={
                      slugLoaded
                        ? 'Shows a Claim {domain} button; signups via that link attribute to your agency.'
                        : 'Shows a Claim {domain} button. Needs a referral link first — create one in Referral.'
                    }
                  />
                  <CtaRadio
                    C={C}
                    value="custom"
                    selected={effectiveCta}
                    onChange={(v) => setDraft((d) => ({ ...d, ctaMode: v }))}
                    label="Your own link"
                    subline={
                      draft.contactUrl.trim().length === 0
                        ? 'Shows a Talk to {Agency} button that opens your contact link. Add a contact link above to use this.'
                        : 'Shows a Talk to {Agency} button that opens your contact link.'
                    }
                  />
                  <CtaRadio
                    C={C}
                    value="none"
                    selected={effectiveCta}
                    onChange={(v) => setDraft((d) => ({ ...d, ctaMode: v }))}
                    label="No button"
                    subline="The report shows a quiet line asking the reader to reply to your email."
                  />
                </div>
                {needsSlugPrompt ? (
                  <div style={{ marginTop: 10 }} data-testid="white-label-cta-slug-prompt">
                    <InlineNotice tone="warn">
                      Create a referral link to use the Sign-up button.{' '}
                      <a
                        href="?view=agencyPortal&section=referral"
                        style={{ color: 'inherit', textDecoration: 'underline' }}
                      >
                        Open Referral →
                      </a>
                    </InlineNotice>
                  </div>
                ) : null}
              </div>
            );
          })()}

          {saveError !== null ? (
            <div style={{ marginTop: 14 }}>
              <InlineNotice tone="error" data-testid="white-label-error">
                {saveError}
              </InlineNotice>
            </div>
          ) : null}

          <div style={{ marginTop: 16 }}>
            <SaveBar
              status={saveStatus}
              savedAt={savedAt}
              canSave={canSave}
              onSave={() => void onSave()}
              onDiscard={onDiscard}
              data-testid="white-label-save-bar"
            />
          </div>
          {!whiteLabelOn ? (
            <div style={{ marginTop: 12 }}>
              <InlineNotice tone="info" data-testid="white-label-feature-off">
                White label is an agency add-on. Only your contact link saves while it's off; the visuals below are a preview.
              </InlineNotice>
            </div>
          ) : null}
        </Card>

        {/* ─── RIGHT: preview with tabs ───────────────────────── */}
        <Card className="p-5 sm:p-6" data-testid="white-label-preview-pane">
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 12 }}>
            <PreviewTabButton active={tab === 'report'} onClick={() => setTab('report')} C={C}>
              Report page
            </PreviewTabButton>
            <PreviewTabButton active={tab === 'email'} onClick={() => setTab('email')} C={C}>
              Email
            </PreviewTabButton>
            {saveStatus === 'dirty' ? (
              <span
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.amberFg,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  marginLeft: 'auto',
                }}
              >
                Previewing unsaved
              </span>
            ) : null}
          </div>
          {tab === 'report' ? (
            <WhiteLabelReportPreview
              displayName={draft.displayName}
              logoUrl={draft.logoUrl.trim() === '' ? null : draft.logoUrl.trim()}
              accentHex={CLIENT_HEX_RE.test(draft.accentHex.trim()) ? draft.accentHex.trim().toLowerCase() : null}
              contactUrl={draft.contactUrl.trim() === '' ? null : draft.contactUrl.trim()}
              ctaMode={draft.ctaMode}
              // v3.2 (2026-10-10) — tell the preview whether the agency
              // has a referral slug; it uses the same smart-default
              // logic as the radio picker so the preview matches the
              // selected option even when `draft.ctaMode` is null.
              hasReferralSlug={typeof referralSlug === 'string' && referralSlug.length > 0}
              whiteLabel={whiteLabelOn}
            />
          ) : (
            <div>
              {emailError !== null ? (
                <div style={{ marginBottom: 10 }}>
                  <InlineNotice tone="warn">{emailError}</InlineNotice>
                </div>
              ) : null}
              {emailHtml !== null ? (
                <iframe
                  // sandbox="" drops ALL privileges — the preview is
                  // pure HTML + inline CSS. srcdoc keeps the iframe
                  // origin-less so no cookie/storage flows into it.
                  sandbox=""
                  // v3 (2026-10-10) — force the iframe to render in
                  // LIGHT. The email template includes a
                  // `prefers-color-scheme: dark` media query so
                  // recipients on dark-mode mail clients see dark
                  // chrome; without this hint the dashboard's dark
                  // theme matched the query inside the sandboxed
                  // iframe and the dashboard preview flipped dark.
                  // `color-scheme: only light` scopes the iframe
                  // itself; the wrapped srcdoc also carries a <meta>
                  // so the HTML document agrees. Real emails sent to
                  // recipients keep their dark-mode behaviour; this
                  // only pins the dashboard preview to light.
                  srcDoc={wrapEmailPreviewLight(emailHtml)}
                  title="Email preview" /* title-ok: accessible name of the iframe (required) */
                  data-testid="white-label-email-preview-frame"
                  style={{
                    width: '100%',
                    // v4 (2026-10-10) — minHeight bumped from 640 so
                    // the branded email's full chrome (brand strip +
                    // header + 8 signal rows + CTA + postal footer) is
                    // visible without scrolling. The sandbox="" iframe
                    // cannot postMessage its content height; a static
                    // ceiling that matches the longest realistic
                    // branded email is the pragmatic fix. The founder
                    // reported "sign-up button is not showing" after
                    // the referralSlug race was fixed — the second
                    // half of that complaint was CTA clipping below
                    // the 640px cut-off.
                    minHeight: 1600,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                    background: '#ffffff',
                    colorScheme: 'only light',
                  }}
                />
              ) : (
                <div
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    color: C.muted,
                    padding: 20,
                    border: `1px dashed ${C.border}`,
                    borderRadius: 2,
                    textAlign: 'center',
                  }}
                >
                  Rendering preview…
                </div>
              )}
            </div>
          )}
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 11,
              color: C.muted,
              marginTop: 10,
              lineHeight: 1.5,
            }}
          >
            This is what your client sees. Sample data; your real reports use their scan.
          </div>
        </Card>
      </div>

      {/* Stack below 1024 px */}
      <style>{`@media (max-width: 1024px) { .wl-grid { grid-template-columns: minmax(0, 1fr) !important; } }`}</style>
    </WhiteLabelShell>
  );
}

/** v3 (2026-10-10) — radio for the Report-button picker. Uses the
 *  shared theme tokens so the selected state matches the SaveBar
 *  "primary" treatment (lime left-bar). */
function CtaRadio({
  C,
  value,
  selected,
  onChange,
  label,
  subline,
}: {
  C: ReturnType<typeof useC>;
  value: ReportCtaMode | null;
  selected: ReportCtaMode | null;
  onChange: (v: ReportCtaMode | null) => void;
  label: string;
  subline: string;
}): JSX.Element {
  const checked = selected === value;
  return (
    <label
      data-testid={`white-label-cta-radio-${value ?? 'default'}`}
      style={{
        display: 'flex',
        gap: 10,
        padding: '10px 12px',
        border: `1px solid ${checked ? C.border : C.borderSoft}`,
        borderLeft: `3px solid ${checked ? C.lime : C.borderSoft}`,
        borderRadius: 2,
        background: checked ? C.surface2 : 'transparent',
        cursor: 'pointer',
      }}
    >
      <input
        type="radio"
        name="wl-cta-mode"
        checked={checked}
        onChange={() => onChange(value)}
        style={{ marginTop: 3 }}
        aria-label={label}
      />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>
          {label}
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>
          {subline}
        </div>
      </div>
    </label>
  );
}

function PreviewTabButton({
  active,
  onClick,
  C,
  children,
}: {
  active: boolean;
  onClick: () => void;
  C: ReturnType<typeof useC>;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: '6px 12px',
        fontFamily: sansFont,
        fontSize: 12,
        fontWeight: active ? 500 : 400,
        color: active ? C.text : C.muted,
        background: active ? C.surface2 : 'transparent',
        border: `1px solid ${active ? C.border : 'transparent'}`,
        borderBottom: active ? `2px solid ${C.lime}` : `1px solid transparent`,
        borderRadius: 2,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function labelStyle(C: ReturnType<typeof useC>): React.CSSProperties {
  return {
    display: 'block',
    fontFamily: monoFont,
    fontSize: 9,
    color: C.muted,
    letterSpacing: 2,
    textTransform: 'uppercase',
  };
}
function inputStyle(C: ReturnType<typeof useC>): React.CSSProperties {
  return {
    display: 'block',
    marginTop: 6,
    width: '100%',
    fontFamily: sansFont,
    fontSize: 14,
    color: C.text,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 3,
    padding: '8px 10px',
    boxSizing: 'border-box',
  };
}
function hintStyle(C: ReturnType<typeof useC>): React.CSSProperties {
  return {
    fontFamily: sansFont,
    fontSize: 11,
    color: C.muted,
    marginTop: 6,
    lineHeight: 1.5,
  };
}

function WhiteLabelShell({ children }: { children: React.ReactNode }): JSX.Element {
  const C = useC();
  const embedded = useIsEmbedded();
  if (embedded) {
    return <>{children}</>;
  }
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="mb-8">
        <NumLabel n={1}>Agency</NumLabel>
        <h1
          style={{
            fontFamily: serifFont,
            fontSize: 'clamp(32px, 5vw, 44px)',
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1.5,
          }}
        >
          White label.
        </h1>
      </div>
      {children}
    </div>
  );
}
